import { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BLOG_NEWS } from '../../data/blogNews.js';

const KNOWN_PLAYERS = [
  { name: 'Magnus Carlsen',        gender: 'male',   fideId: '1503014',  federation: 'NOR' },
  { name: 'Gukesh Dommaraju',      gender: 'male',   fideId: '46616543', federation: 'IND' },
  { name: 'Arjun Erigaisi',        gender: 'male',   fideId: '35009192', federation: 'IND' },
  { name: 'R. Praggnanandhaa',     gender: 'male',   fideId: '25059530', federation: 'IND' },
  { name: 'Nihal Sarin',           gender: 'male',   fideId: '25092340', federation: 'IND' },
  { name: 'Fabiano Caruana',       gender: 'male',   fideId: '2020009',  federation: 'USA' },
  { name: 'Hikaru Nakamura',       gender: 'male',   fideId: '2016192',  federation: 'USA' },
  { name: 'Nodirbek Abdusattorov', gender: 'male',   fideId: '14204118', federation: 'UZB' },
  { name: 'Anish Giri',            gender: 'male',   fideId: '24116068', federation: 'NED' },
  { name: 'Wei Yi',                gender: 'male',   fideId: '8603677',  federation: 'CHN' },
  { name: 'Javokhir Sindarov',     gender: 'male',   fideId: '14204860', federation: 'UZB' },
  { name: 'Vaishali Rameshbabu',   gender: 'female', fideId: '25059505', federation: 'IND' },
  { name: 'Divya Deshmukh',        gender: 'female', fideId: '25059564', federation: 'IND' },
  { name: 'Koneru Humpy',          gender: 'female', fideId: '5008018',  federation: 'IND' },
  { name: 'Aravindh Chithambaram', gender: 'male',   fideId: '25059947', federation: 'IND' },
];

const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const GlobalSearch = () => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return { players: [], posts: [] };

    const players = KNOWN_PLAYERS.filter((p) =>
      p.name.toLowerCase().includes(q) || p.federation.toLowerCase().includes(q)
    ).slice(0, 4);

    const matchedPosts = BLOG_NEWS.filter((p) =>
      p.title.toLowerCase().includes(q) ||
      p.keywords?.some((k) => k.toLowerCase().includes(q))
    ).slice(0, 4).map((p) => ({
      slug: p.url.split('/').pop(),
      title: p.title,
      url: p.url,
    }));

    return { players, posts: matchedPosts };
  }, [query]);

  const hasResults = results.players.length > 0 || results.posts.length > 0;

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close on Escape, open on /
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') { setOpen(false); setQuery(''); }
      if (e.key === '/' && document.activeElement !== inputRef.current) {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    navigate(`/players?q=${encodeURIComponent(query.trim())}`);
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={containerRef} className="relative">
      <form onSubmit={handleSubmit}>
        <div className="relative flex items-center">
          <span className="absolute left-2.5 text-zinc-400 dark:text-zinc-500 pointer-events-none">
            <SearchIcon />
          </span>
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder="Search players, articles…"
            className="h-10 w-28 sm:w-52 pl-8 pr-2 text-xs rounded-lg bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-200 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-amber-400 dark:focus:ring-amber-500 transition-all"
          />
        </div>
      </form>

      {open && query.length >= 2 && (
        <div className="absolute right-0 top-10 w-72 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl shadow-lg z-50 overflow-hidden">
          {!hasResults ? (
            <p className="text-xs text-zinc-400 px-4 py-3">No results for "{query}"</p>
          ) : (
            <>
              {results.players.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-zinc-400 dark:text-zinc-500 px-4 pt-3 pb-1">Players</p>
                  {results.players.map((p) => (
                    <Link
                      key={p.fideId}
                      to={`/players/${p.gender}/${p.fideId}`}
                      onClick={() => { setOpen(false); setQuery(''); }}
                      className="flex items-center justify-between px-4 min-h-[44px] hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                    >
                      <span className="text-sm text-zinc-800 dark:text-zinc-200">{p.name}</span>
                      <span className="text-xs text-zinc-400">{p.federation}</span>
                    </Link>
                  ))}
                </div>
              )}
              {results.posts.length > 0 && (
                <div className={results.players.length > 0 ? 'border-t border-zinc-100 dark:border-zinc-800' : ''}>
                  <p className="text-[10px] uppercase tracking-wider text-zinc-400 dark:text-zinc-500 px-4 pt-3 pb-1">Articles</p>
                  {results.posts.map((post) => (
                    <a
                      key={post.slug}
                      href={post.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => { setOpen(false); setQuery(''); }}
                      className="flex items-center px-4 min-h-[44px] hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                    >
                      <span className="text-sm text-zinc-800 dark:text-zinc-200 line-clamp-1">{post.title}</span>
                    </a>
                  ))}
                </div>
              )}
              <div className="border-t border-zinc-100 dark:border-zinc-800 px-4 py-2">
                <Link
                  to={`/players?q=${encodeURIComponent(query.trim())}`}
                  onClick={() => { setOpen(false); setQuery(''); }}
                  className="text-xs text-amber-600 dark:text-amber-400 hover:underline"
                >
                  Search all players for "{query}" →
                </Link>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
